import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FiHome, FiDollarSign, FiCalendar, FiTrendingUp, FiAlertCircle, FiUser, FiMessageCircle } from 'react-icons/fi';
import api from '../services/api';
import './Dashboard.css';

const OwnerDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [ownershipRequests, setOwnershipRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user?.is_owner) {
      navigate('/');
      return;
    }
    fetchDashboard();
    fetchOwnershipRequests();
  }, [user, navigate]);

  const fetchDashboard = async () => {
    try {
      const response = await api.get('/api/owners/dashboard');
      setDashboard(response.data);
    } catch (error) {
      console.error('Error fetching owner dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnershipRequests = async () => {
    try {
      const response = await api.get('/api/owners/ownership-requests');
      setOwnershipRequests(response.data.ownership_requests || []);
    } catch (error) {
      console.error('Error fetching ownership requests:', error);
    }
  };

  const handleApproveRequest = async (requestId) => {
    if (!window.confirm('Deseja aprovar este pedido de gestão?')) return;

    try {
      await api.post(`/api/owners/ownership-requests/${requestId}/approve`);
      alert('Pedido aprovado com sucesso!');
      fetchOwnershipRequests();
      fetchDashboard();
    } catch (error) {
      console.error('Error approving request:', error);
      alert('Erro ao aprovar pedido');
    }
  };

  const handleRejectRequest = async (requestId) => {
    if (!window.confirm('Deseja rejeitar este pedido de gestão?')) return;

    try {
      await api.post(`/api/owners/ownership-requests/${requestId}/reject`);
      alert('Pedido rejeitado');
      fetchOwnershipRequests();
    } catch (error) {
      console.error('Error rejecting request:', error);
      alert('Erro ao rejeitar pedido');
    }
  };

  const pendingRequests = ownershipRequests.filter(r => r.status === 'pending');

  if (loading) {
    return <div className="dashboard-container"><p>Carregando...</p></div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Painel do Proprietário</h1>
        <p>Bem-vindo, {user?.first_name}!</p>
      </div>

      {/* Pending Authorization Requests */}
      {pendingRequests.length > 0 && (
        <div className="alert alert-warning" style={{
          marginBottom: '2rem',
          background: '#fff8e1',
          border: '2px solid #ffc107',
          borderRadius: '12px',
          padding: '1.5rem'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
            <FiAlertCircle size={24} color="#f57c00" />
            <h3 style={{ margin: 0 }}>Pedidos de Autorização Pendentes ({pendingRequests.length})</h3>
          </div>
          {pendingRequests.map(request => (
            <div key={request.id} style={{
              background: 'white',
              padding: '1.5rem',
              marginTop: '1rem',
              borderRadius: '8px',
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
              <p><strong>{request.host?.first_name} {request.host?.last_name}</strong> quer gerenciar sua propriedade:</p>
              <p style={{ fontSize: '1.1rem', fontWeight: '600', color: '#333' }}>{request.property?.title}</p>
              {request.message && (
                <p style={{ color: '#666', fontSize: '0.9rem', fontStyle: 'italic' }}>{request.message}</p>
              )}
              <div style={{ marginTop: '1rem', display: 'flex', gap: '1rem' }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => handleApproveRequest(request.id)}
                >
                  Aprovar
                </button>
                <button
                  className="btn btn-secondary btn-sm"
                  onClick={() => handleRejectRequest(request.id)}
                >
                  Rejeitar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Summary Cards */}
      <div className="dashboard-stats">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e3f2fd' }}>
            <FiHome size={24} color="#1976d2" />
          </div>
          <div className="stat-content">
            <h3>Imóveis</h3>
            <p className="stat-number">{dashboard?.summary?.total_properties || 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e8f5e9' }}>
            <FiDollarSign size={24} color="#388e3c" />
          </div>
          <div className="stat-content">
            <h3>Receita Total</h3>
            <p className="stat-number">R$ {dashboard?.summary?.total_revenue?.toLocaleString('pt-BR') || '0,00'}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fff3e0' }}>
            <FiCalendar size={24} color="#f57c00" />
          </div>
          <div className="stat-content">
            <h3>Total de Reservas</h3>
            <p className="stat-number">{dashboard?.summary?.total_bookings || 0}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#f3e5f5' }}>
            <FiTrendingUp size={24} color="#7b1fa2" />
          </div>
          <div className="stat-content">
            <h3>Receita Média/Imóvel</h3>
            <p className="stat-number">R$ {dashboard?.summary?.average_revenue_per_property?.toLocaleString('pt-BR') || '0,00'}</p>
          </div>
        </div>
      </div>

      {/* Properties List */}
      <div className="dashboard-section">
        <h2>Meus Imóveis</h2>
        {dashboard?.properties && dashboard.properties.length > 0 ? (
          <div className="properties-grid">
            {dashboard.properties.map(property => (
              <div key={property.id} className="property-card" style={{ position: 'relative' }}>
                <div style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  padding: '0.375rem 0.75rem',
                  borderRadius: '20px',
                  fontSize: '0.75rem',
                  fontWeight: '600',
                  background: property.is_active ? '#d4edda' : '#f8d7da',
                  color: property.is_active ? '#155724' : '#721c24'
                }}>
                  {property.is_active ? 'ATIVO' : 'INATIVO'}
                </div>
                <h3 style={{ paddingRight: '5rem' }}>{property.title}</h3>
                <p style={{ color: '#666', marginBottom: '0.5rem' }}>{property.city}</p>

                {/* Host Information */}
                {property.host && (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.5rem',
                    marginBottom: '1rem',
                    padding: '0.5rem',
                    background: '#f8f9fa',
                    borderRadius: '6px'
                  }}>
                    <FiUser size={14} color="#666" />
                    <span style={{ fontSize: '0.85rem', color: '#666' }}>
                      Host: <strong>{property.host.first_name} {property.host.last_name}</strong>
                    </span>
                    <button
                      onClick={() => navigate('/messages')}
                      style={{
                        marginLeft: 'auto',
                        background: 'none',
                        border: 'none',
                        color: '#007bff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.25rem',
                        fontSize: '0.85rem',
                        padding: '0.25rem'
                      }}
                      title="Enviar mensagem"
                    >
                      <FiMessageCircle size={14} />
                    </button>
                  </div>
                )}

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: '0.75rem',
                  marginTop: '1rem',
                  paddingTop: '1rem',
                  borderTop: '1px solid #e0e0e0'
                }}>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>Receita</p>
                    <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#28a745', margin: '0.25rem 0 0 0' }}>
                      R$ {property.revenue?.toLocaleString('pt-BR') || '0'}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>Reservas</p>
                    <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#333', margin: '0.25rem 0 0 0' }}>
                      {property.bookings_count || 0}
                    </p>
                  </div>
                  <div>
                    <p style={{ fontSize: '0.75rem', color: '#666', margin: 0 }}>Próximas</p>
                    <p style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#007bff', margin: '0.25rem 0 0 0' }}>
                      {property.upcoming_bookings || 0}
                    </p>
                  </div>
                </div>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => navigate(`/owner/properties/${property.id}`)}
                  style={{ marginTop: '1rem', width: '100%' }}
                >
                  Ver Detalhes
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p style={{
            textAlign: 'center',
            color: '#666',
            padding: '2rem',
            background: '#f8f9fa',
            borderRadius: '8px'
          }}>
            Você ainda não possui imóveis cadastrados.
          </p>
        )}
      </div>

      {/* Upcoming Bookings */}
      {dashboard?.upcoming_bookings && dashboard.upcoming_bookings.length > 0 && (
        <div className="dashboard-section">
          <h2>Próximas Reservas</h2>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Imóvel</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Hóspede</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Check-in</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Check-out</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Noites</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Valor</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.upcoming_bookings.map(booking => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '0.75rem' }}>{booking.property?.title}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {booking.guest?.first_name} {booking.guest?.last_name}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(booking.check_in).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(booking.check_out).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{booking.nights}</td>
                    <td style={{ padding: '0.75rem', fontWeight: '600', color: '#28a745' }}>
                      R$ {booking.total_price?.toLocaleString('pt-BR')}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: '#d1ecf1',
                        color: '#0c5460'
                      }}>
                        {booking.status === 'confirmed' ? 'Confirmada' : booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Past Bookings */}
      {dashboard?.past_bookings && dashboard.past_bookings.length > 0 && (
        <div className="dashboard-section">
          <h2>Reservas Passadas</h2>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Imóvel</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Hóspede</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Check-in</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Check-out</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Noites</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Valor</th>
                  <th style={{ padding: '0.75rem', textAlign: 'left', fontWeight: '600' }}>Status</th>
                </tr>
              </thead>
              <tbody>
                {dashboard.past_bookings.map(booking => (
                  <tr key={booking.id} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '0.75rem' }}>{booking.property?.title}</td>
                    <td style={{ padding: '0.75rem' }}>
                      {booking.guest?.first_name} {booking.guest?.last_name}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(booking.check_in).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      {new Date(booking.check_out).toLocaleDateString('pt-BR')}
                    </td>
                    <td style={{ padding: '0.75rem' }}>{booking.nights}</td>
                    <td style={{ padding: '0.75rem', fontWeight: '600', color: '#28a745' }}>
                      R$ {booking.total_price?.toLocaleString('pt-BR')}
                    </td>
                    <td style={{ padding: '0.75rem' }}>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: '600',
                        background: booking.status === 'completed' ? '#d4edda' : '#f8d7da',
                        color: booking.status === 'completed' ? '#155724' : '#721c24'
                      }}>
                        {booking.status === 'completed' ? 'Concluída' : booking.status === 'cancelled' ? 'Cancelada' : booking.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div style={{ marginTop: '2rem' }}>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/owner/properties/new')}
        >
          Cadastrar Novo Imóvel
        </button>
      </div>
    </div>
  );
};

export default OwnerDashboard;
